import { LayoutConfig } from 'golden-layout';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  GLayout,
  type GLayoutRef
} from '@pasqal/core/ui/components/pulser-studio/components/layout/GLayout';
import { useHelp } from '~/systems/help';
import { useUiState } from '~/systems/uiState';
import { createLocalStorageSynchronized } from '~/util/persistence';
import * as panels from '../panels';
import type { ComponentContainer, ResolvedLayoutConfig } from 'golden-layout';
import type { ReactElement } from 'react';

// 296px is the narrowest a panel can get before an editor row breaks
const PANEL_MIN_WIDTH = '296px';

const initialConfig: LayoutConfig = {
  root: {
    type: 'column',
    content: [
      {
        type: 'row',
        content: [
          // Explicit stacks keep minSize on the component: golden-layout sums the
          // row's stack minSizes as its min height, which blocks the Channels splitter
          {
            type: 'stack',
            content: [
              {
                type: 'component',
                title: 'Register',
                componentType: 'Register',
                minSize: PANEL_MIN_WIDTH
              }
            ]
          },
          {
            type: 'stack',
            content: [
              {
                type: 'component',
                title: 'Histogram',
                componentType: 'Histogram',
                minSize: PANEL_MIN_WIDTH
              },
              {
                type: 'component',
                title: 'Quantum State',
                componentType: 'QuantumState'
              }
            ]
          },
          {
            type: 'stack',
            content: [
              {
                type: 'component',
                title: 'Code',
                componentType: 'Code',
                minSize: PANEL_MIN_WIDTH
              }
            ]
          }
        ]
      },
      {
        type: 'component',
        title: 'Channels',
        componentType: 'Channels'
      }
    ]
  },
  settings: {
    constrainDragToContainer: false,
    reorderEnabled: true,
    popoutWholeStack: false,
    blockedPopoutsThrowError: true,
    closePopoutsOnUnload: true,
    responsiveMode: 'always',
    tabOverlapAllowance: 0,
    reorderOnTabMenuClick: true,
    tabControlOffset: 10,
    popInOnClose: false
  },
  dimensions: {
    borderWidth: 3,
    borderGrabWidth: 10,
    minItemHeight: 150,
    minItemWidth: 150,
    headerHeight: 28,
    dragProxyWidth: 400,
    dragProxyHeight: 300
  },
  header: {
    show: 'top',
    popout: false,
    close: 'close',
    maximise: false,
    tabDropdown: 'additional tabs'
  }
};

const layoutConfig = createLocalStorageSynchronized<
  LayoutConfig | ResolvedLayoutConfig
>('exp7-layout', initialConfig);

export default function LayoutRoot() {
  const glayoutRef = useRef<GLayoutRef | null>(null);
  const { focusPanel } = useUiState();
  const { getAvailableElement, mountElement, onTrigger, unmountElement } =
    useHelp();
  const helpBoundMapRef = useRef(new Map<ComponentContainer, HTMLElement>());

  const updateHelpBindings = useCallback(() => {
    const entries = [...helpBoundMapRef.current.entries()];
    for (const [component, prevElement] of entries) {
      const item = component.parent;
      const componentName = component.componentType;
      const element = item.parent?.isStack ? item.parent.element : item.element;
      if (element !== prevElement) {
        helpBoundMapRef.current.delete(component);
        unmountElement(prevElement, `tab-${componentName}`);
        helpBoundMapRef.current.set(component, element);
        mountElement(element, `tab-${componentName}`);
      }
    }
  }, [mountElement, unmountElement]);

  const onBind = useCallback(
    (component: ComponentContainer) => {
      const item = component.parent;
      const element = item.parent?.isStack ? item.parent.element : item.element;
      helpBoundMapRef.current.set(component, element);
      mountElement(element, `tab-${component.componentType}`);
    },
    [mountElement]
  );

  const onUnbind = useCallback(
    (component: ComponentContainer) => {
      const element = helpBoundMapRef.current.get(component);
      if (element) {
        helpBoundMapRef.current.delete(component);
        unmountElement(element, `tab-${component.componentType}`);
      }
    },
    [unmountElement]
  );

  // Set up help trigger handler
  useEffect(() => {
    return onTrigger((id) => {
      if (id.startsWith('tab-')) {
        const tabId = id.substring(4);
        const helpEntry = getAvailableElement(id);
        if (!helpEntry) {
          return;
        }
        for (const [component, element] of helpBoundMapRef.current) {
          if (helpEntry === element && component.componentType === tabId) {
            component.focus();
            return;
          }
        }
      }
    });
  }, [getAvailableElement, onTrigger]);

  // Cleanup help bindings on unmount
  useEffect(() => {
    const currentHelpBoundMapRef = helpBoundMapRef.current;
    return () => {
      currentHelpBoundMapRef.forEach((element, component) => {
        unmountElement(element, `tab-${component.componentType}`);
      });
      currentHelpBoundMapRef.clear();
    };
  }, [unmountElement]);

  // Initialize layout once on mount
  useEffect(() => {
    const glayout = glayoutRef.current;
    if (!glayout) {
      return;
    }

    const layout = glayout.rawLayout();

    layout.on('focus', (event) => {
      const panel = (event.target as ComponentContainer)
        .componentType as string;
      focusPanel(panel);
    });

    layout.on('stateChanged', () => {
      const resolvedConfig = glayout.getLayoutConfig();
      if (resolvedConfig) {
        const config = LayoutConfig.fromResolved(resolvedConfig);
        if (!isEqual(config, layoutConfig.get())) {
          layoutConfig.set(config);
        }
      }
      updateHelpBindings();
    });

    const config = { ...layoutConfig.get() };
    config.settings = initialConfig.settings;
    config.dimensions = initialConfig.dimensions;
    glayout.loadLayout(config);
  }, [focusPanel, updateHelpBindings]);

  // Build component map: name → render function
  // Must be stable (useMemo) to prevent GLayout from seeing new references
  // and remounting panel components on every stateChanged event.
  const componentMap = useMemo<
    Record<
      string,
      (props: { headerNode: HTMLElement | null }) => ReactElement | null
    >
  >(
    () => ({
      Channels: (props) => <panels.Channels {...props} />,
      Code: (props) => <panels.Code {...props} />,
      Histogram: (props) => <panels.Histogram {...props} />,
      QuantumState: (props) => <panels.QuantumState {...props} />,
      Register: (props) => <panels.Register {...props} />
    }),
    []
  );

  return (
    <GLayout
      ref={glayoutRef}
      components={componentMap}
      onBind={onBind}
      onUnbind={onUnbind}
    />
  );
}
